import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowUpRight, ChevronDown, ChevronRight, Receipt } from "lucide-react";
import { formatMoney, formatDate } from "@/lib/api";
import { displayFor } from "@/lib/bills-util";

// Section palette — deeper contrast per user request. Each palette also
// carries an optional `manageLink` so the section header doubles as a
// shortcut into the corresponding management page (Accounts, Income, Bills).
// This lets users who prefer the monthly view still edit source data with
// one click instead of hunting through the sidebar.
const SECTION = {
  incoming: {
    bg: "bg-[#DDE9D8]",
    border: "border-[#B8CFB1]",
    label: "text-[#2F4E32]",
    amount: "text-[#2F4E32]",
    header: "Incoming money",
    manageLink: "/app/income",
    manageLabel: "Manage income",
  },
  bills: {
    bg: "bg-[#F5D9D1]",
    border: "border-[#E5B4A3]",
    label: "text-[#7A2E24]",
    amount: "text-[#7A2E24]",
    header: "Bills owed",
    manageLink: "/app/bills",
    manageLabel: "Manage bills",
  },
  bank: {
    bg: "bg-[#E8E4DC]",
    border: "border-[#CDC7BB]",
    label: "text-[#3E4340]",
    amount: "text-[#2C302E]",
    header: "Money on hand",
    manageLink: "/app/accounts",
    manageLabel: "Manage accounts",
  },
  final: {
    bg: "bg-[#FCE3C6]",
    border: "border-[#EBC7A0]",
    label: "text-[#7A4E1F]",
    header: "End-of-month forecast",
  },
};

/**
 * Compute all the numbers rendered by MonthProjection.
 * Returns an object with incoming/bills/bank breakdowns and the projected EOM.
 * Exported so parent screens can chain months (use one month's `eom` as the
 * next month's `startingBalance`).
 */
export function computeMonthForecast({
  monthStart, monthEnd, isCurrentMonth,
  bills = [], incomes = [], pending = [], fundingSources = [],
  currentBalance = 0, startingBalance = 0,
}) {
  const today = new Date(); today.setHours(0, 0, 0, 0);

  // Incoming — include real rows in the month window PLUS, for future months,
  // synthesized occurrences of every recurring income series (weekly/biweekly/
  // monthly) that lands inside the window. Without this, future-month cards
  // showed only one-off incomes and drastically underestimated projected
  // paychecks (bug reported 2026-02).
  const FREQ_STEP = { weekly: 7, biweekly: 14, monthly: 30 };
  const parseIso = (s) => { const d = new Date(s + "T00:00:00"); d.setHours(0, 0, 0, 0); return d; };
  const isoOf = (d) => d.toISOString().slice(0, 10);

  const realInWindow = incomes.filter((i) => {
    if (i.status !== "expected") return false;
    if (!i.expected_date) return false;
    const d = parseIso(i.expected_date);
    return d >= monthStart && d <= monthEnd;
  });

  // Real income dates already covered in this window — projection MUST skip
  // these so it doesn't double-count the anchor row of a recurring series.
  // Key by "sourceName|isoDate" so overlapping series with different names
  // aren't collapsed together. IMPORTANT: also include RECEIVED income rows
  // so a paycheck the user has already marked received doesn't get resurrected
  // as a projected virtual occurrence for the same date (Aug-14 bug 2026-02).
  const receivedInWindow = incomes.filter((i) => {
    if (i.status !== "received") return false;
    if (!i.expected_date) return false;
    const d = parseIso(i.expected_date);
    return d >= monthStart && d <= monthEnd;
  });
  const realDateKeys = new Set([
    ...realInWindow.map((i) => `${(i.source || "").toLowerCase()}|${i.expected_date}`),
    ...receivedInWindow.map((i) => `${(i.source || "").toLowerCase()}|${i.expected_date}`),
  ]);

  // Recurring incomes are synthesized for EVERY month — including the current
  // one. Previously we only projected future months, which meant a biweekly
  // paycheck whose next expected date fell mid-month wouldn't appear if only
  // the earlier occurrence was stored. Bug reported 2026-02.
  let projected = [];
  for (const inc of incomes) {
    const step = FREQ_STEP[inc.frequency];
    if (!step) continue; // only recurring series
    const base = parseIso(inc.expected_date);
    const endInc = inc.end_date ? parseIso(inc.end_date) : null;
    const overrides = inc.overrides || {};
    const skipDates = new Set(inc.skip_dates || []);
    let cur = new Date(base);
    // fast-forward or rewind to first occurrence >= monthStart
    while (cur < monthStart) cur.setDate(cur.getDate() + step);
    // Walk BACKWARD only if we're inside the window AND still after the
    // base — recurring occurrences never exist before the base date, so a
    // Nov-9 base must not project into Sep/Oct (bug 2026-02).
    while (cur > monthStart && cur > base) {
      const prev = new Date(cur); prev.setDate(prev.getDate() - step);
      if (prev < monthStart) break;
      cur = prev;
    }
    while (cur <= monthEnd) {
      if (endInc && cur > endInc) break;
      const iso = isoOf(cur);
      const key = `${(inc.source || "").toLowerCase()}|${iso}`;
      if (!realDateKeys.has(key) && !skipDates.has(iso)) {
        const amt = Number(overrides[iso] ?? inc.amount);
        projected.push({
          id: `virtual-${inc.id}-${iso}`,
          source: inc.source,
          amount: amt,
          frequency: inc.frequency,
          expected_date: iso,
          status: "expected",
          is_virtual: true,
        });
      }
      cur = new Date(cur); cur.setDate(cur.getDate() + step);
    }
  }

  const incoming = [...realInWindow, ...projected]
    .sort((a, b) => a.expected_date.localeCompare(b.expected_date));
  const incomingTotal = incoming.reduce((s, i) => s + Number(i.amount), 0);

  // Prior-month unpaid bills (only meaningful for the current month)
  const priorBills = isCurrentMonth
    ? bills.filter((b) => {
        if (["paid", "cleared"].includes(b.status)) return false;
        if (displayFor(b, today).notCleared) return false;
        const d = new Date(b.due_date + "T00:00:00");
        return d < monthStart;
      })
    : [];
  const priorBillsTotal = priorBills.reduce(
    (s, b) => s + Math.max(0, Number(b.estimated_due_amount) - Number(b.paid_amount || 0)),
    0
  );

  // Current-month unpaid bills
  const inMonth = (b) => {
    const d = new Date(b.due_date + "T00:00:00");
    return d >= monthStart && d <= monthEnd;
  };
  const currentMonthBills = bills.filter((b) => {
    if (!inMonth(b)) return false;
    if (isCurrentMonth) {
      if (["paid", "cleared"].includes(b.status)) return false;
      if (displayFor(b, today).notCleared) return false;
      return true;
    }
    return true;
  });
  const currentMonthTotal = currentMonthBills.reduce(
    (s, b) => s + Math.max(0, Number(b.estimated_due_amount) - Number(b.paid_amount || 0)),
    0
  );
  const totalUnpaid = priorBillsTotal + currentMonthTotal;

  // Bank accounts + pending
  const sourcesWithBalances = isCurrentMonth
    ? fundingSources.filter((f) => f.contributes_to_total_balance && f.current_source_balance_optional != null)
    : [];
  // Bills that are already out of the bank but not yet cleared (pending / autopay past-due / paid-uncleared).
  // These reduce the available bank balance because the money has left the account.
  const notClearedBills = isCurrentMonth
    ? bills.filter((b) => displayFor(b, today).notCleared)
    : [];
  const notClearedTotal = notClearedBills.reduce(
    (s, b) => s + Number(b.estimated_due_amount),
    0
  );
  const pendingBySource = new Map();
  let pendingUnassigned = 0;
  if (isCurrentMonth) {
    // Explicit pending payments
    for (const p of pending) {
      const amt = Number(p.amount);
      if (p.funding_source_id && sourcesWithBalances.find((s) => s.id === p.funding_source_id)) {
        pendingBySource.set(p.funding_source_id, (pendingBySource.get(p.funding_source_id) || 0) + amt);
      } else {
        pendingUnassigned += amt;
      }
    }
    // Bills marked Pending / not-yet-cleared — count as pending too
    for (const b of notClearedBills) {
      const amt = Number(b.estimated_due_amount);
      if (b.funding_source_id && sourcesWithBalances.find((s) => s.id === b.funding_source_id)) {
        pendingBySource.set(b.funding_source_id, (pendingBySource.get(b.funding_source_id) || 0) + amt);
      } else {
        pendingUnassigned += amt;
      }
    }
  }
  const bankTotalRaw = sourcesWithBalances.length > 0
    ? sourcesWithBalances.reduce((s, f) => s + Number(f.current_source_balance_optional), 0)
    : currentBalance;
  const explicitPendingTotal = isCurrentMonth ? pending.reduce((s, p) => s + Number(p.amount), 0) : 0;
  const pendingTotal = explicitPendingTotal + notClearedTotal;
  const availableBank = isCurrentMonth ? bankTotalRaw - pendingTotal : startingBalance;

  const eom = availableBank + incomingTotal - totalUnpaid;

  return {
    incoming, incomingTotal,
    priorBills, priorBillsTotal,
    currentMonthBills, currentMonthTotal,
    totalUnpaid,
    sourcesWithBalances, pendingBySource, pendingUnassigned,
    bankTotalRaw, pendingTotal, availableBank,
    eom,
  };
}

/**
 * Renders one month's cash-flow projection with color-blocked sections:
 *   [Incoming] → [Bills] → [Bank accounts w/ pending] → [Final EOM forecast]
 *
 * Props:
 *   monthLabel       string   e.g. "This month · July 2026"
 *   monthStart       Date
 *   monthEnd         Date
 *   isCurrentMonth   bool     Enables bank-accounts breakdown (only makes sense for month 0)
 *   bills            array
 *   incomes          array
 *   pending          array    Only used when isCurrentMonth
 *   fundingSources   array    Only used when isCurrentMonth
 *   currentBalance   number   Fallback bank total when no per-source balances (isCurrentMonth only)
 *   startingBalance  number   Projected starting balance (used when !isCurrentMonth)
 *   status           string   safe / tight / short (drives Final section color)
 *   testId           string
 */
export default function MonthProjection({
  monthLabel, monthStart, monthEnd, isCurrentMonth,
  bills = [], incomes = [], pending = [], fundingSources = [],
  currentBalance = 0, startingBalance = 0, status = "safe", testId,
  extraPaycheck = false, paycheckCount = 0, paycheckDates = [],
}) {
  const {
    incoming, incomingTotal,
    priorBills, priorBillsTotal,
    currentMonthBills, currentMonthTotal, totalUnpaid,
    sourcesWithBalances, pendingBySource, pendingUnassigned,
    availableBank, eom,
  } = computeMonthForecast({
    monthStart, monthEnd, isCurrentMonth,
    bills, incomes, pending, fundingSources, currentBalance, startingBalance,
  });

  const finalStatus = status || (eom < 0 ? "short" : eom < 100 ? "tight" : "safe");
  // For future months, we compute Month Net = incoming − bills. No bank balance
  // (we intentionally don't chain a fabricated EOM forward).
  const monthNet = incomingTotal - totalUnpaid;

  // Extra-paycheck banner tooltip lists every paycheck date in this month
  // so the user can see exactly WHICH weeks give them the windfall.
  const extraPaycheckTitle = paycheckDates && paycheckDates.length > 0
    ? `Paycheck dates this month:\n${paycheckDates.map((p) => `• ${p.date}${p.source ? ` — ${p.source}` : ""}`).join("\n")}`
    : "";

  return (
    <section className="card-soft !p-0 overflow-hidden" data-testid={testId}>
      <div className="px-6 pt-6 pb-4 border-b border-[#EBE6E0] flex items-baseline justify-between flex-wrap gap-2">
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D]">Cash-flow forecast</div>
          <h2 className="font-heading text-xl font-medium text-[#2C302E] mt-1">{monthLabel}</h2>
          {extraPaycheck && (
            <div
              className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#FEF3C7] text-[#7A4E1F] border border-[#F0DDA0]"
              title={extraPaycheckTitle}
              data-testid={`${testId}-extra-paycheck-badge`}
            >
              <span aria-hidden="true">🎉</span>
              Extra paycheck month · {paycheckCount} paydays
            </div>
          )}
        </div>
      </div>

      {/* Section: Money on Hand — CURRENT month only. Future months don't get a
          fabricated starting balance because we don't track every purchase, so
          any rolled-forward EOM would be fiction. See PRD 2026-02 decision. */}
      {isCurrentMonth && (
        <Section palette={SECTION.bank} testId={`${testId}-bank`}>
          {sourcesWithBalances.length > 0 ? (
            sourcesWithBalances.map((s) => {
              const pendingForS = pendingBySource.get(s.id) || 0;
              return (
                <div key={s.id}>
                  <Row label={s.name}
                    sublabel={(s.type || "checking").replace("_", " ")}
                    amount={Number(s.current_source_balance_optional)}
                    sign="" palette={SECTION.bank}
                    testId={`${testId}-source-${s.id}`} />
                  {pendingForS > 0 && (
                    <SubRow
                      label="Pending to clear"
                      sublabel="Charges not yet cleared"
                      amount={pendingForS} sign="-"
                      palette={SECTION.bank}
                      testId={`${testId}-source-pending-${s.id}`}
                    />
                  )}
                </div>
              );
            })
          ) : (
            <Row label="Current bank balance"
              sublabel="All accounts combined"
              amount={currentBalance} sign="" palette={SECTION.bank}
              testId={`${testId}-source-balance`} />
          )}
          {pendingUnassigned > 0 && (
            <SubRow label="Pending to clear (unassigned)" amount={pendingUnassigned} sign="-"
              palette={SECTION.bank} testId={`${testId}-pending-unassigned`} />
          )}
          <TotalRow label="Available bank balance"
            amount={availableBank} sign="" palette={SECTION.bank}
            testId={`${testId}-available-bank`} />
        </Section>
      )}

      {/* Section: Incoming */}
      <Section palette={SECTION.incoming} testId={`${testId}-incoming`}>
        {incoming.length === 0 ? (
          <MutedRow label="There are no income or money deposits scheduled for the rest of this pay period." />
        ) : (
          incoming.map((i) => (
            <Row key={i.id} label={i.source}
              sublabel={`${i.frequency} · ${formatDate(i.expected_date)}`}
              amount={Number(i.amount)} sign="+" palette={SECTION.incoming}
              testId={`${testId}-income-${i.id}`} />
          ))
        )}
        <TotalRow label="Total incoming" amount={incomingTotal} sign="+" palette={SECTION.incoming} testId={`${testId}-incoming-total`} />
      </Section>

      {/* Section: Bills */}
      <Section palette={SECTION.bills} testId={`${testId}-bills`}>
        <ExpandableBillsRow
          label="Prior month bills"
          sublabel={isCurrentMonth
            ? `${priorBills.length} bill${priorBills.length === 1 ? "" : "s"} carried from before this month`
            : "Assumed cleared by this month"}
          amount={priorBillsTotal}
          bills={priorBills}
          palette={SECTION.bills}
          testId={`${testId}-prior-bills`}
        />
        <ExpandableBillsRow
          label="Current month unpaid bills"
          sublabel={`${currentMonthBills.length} bill${currentMonthBills.length === 1 ? "" : "s"} due in ${monthLabel.split("·").pop().trim() || "this month"}`}
          amount={currentMonthTotal}
          bills={currentMonthBills}
          palette={SECTION.bills}
          testId={`${testId}-current-unpaid`}
        />
        <TotalRow label="Total unpaid bills" amount={totalUnpaid} sign="-" palette={SECTION.bills} testId={`${testId}-unpaid-total`} />
      </Section>

      {/* Section: Final — current month shows real EOM (bank + income − bills).
          Future months show a Month Net (income − bills) because we can't know
          the true starting balance a month from now. */}
      <div className={`${SECTION.final.bg} border-t-2 ${SECTION.final.border}`}>
        <div className="px-6 py-2 text-[11px] font-semibold uppercase tracking-widest text-[#8C5D30]">
          {isCurrentMonth ? SECTION.final.header : "Month net"}
        </div>
        <div className={`px-6 pb-5 pt-1 flex items-end justify-between gap-4 status-${finalStatus} rounded-b-2xl`}
          data-testid={`${testId}-eom`}>
          <div>
            <div className="text-sm text-[#59605C]">
              {isCurrentMonth
                ? "Available + incoming − bills owed ="
                : "Incoming − bills owed ="}
            </div>
            <div className="text-xs text-[#8A918D] mt-0.5">
              {isCurrentMonth
                ? "Projected balance at end of month"
                : "Do this month's paychecks cover this month's bills?"}
            </div>
          </div>
          <div className="font-heading text-3xl font-semibold whitespace-nowrap"
            style={{ color: (isCurrentMonth ? eom : monthNet) < 0 ? "#8C3A30" : "#3B5B3E" }}>
            {formatMoney(isCurrentMonth ? eom : monthNet)}
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Sub-components ----------
function Section({ palette, testId, children }) {
  const label = palette.header;
  const header = palette.manageLink ? (
    <Link
      to={palette.manageLink}
      className={`px-6 py-2 text-[11px] font-semibold uppercase tracking-widest ${palette.label} inline-flex items-center gap-1 hover:underline underline-offset-2 decoration-dotted`}
      title={palette.manageLabel}
      data-testid={testId ? `${testId}-manage-link` : undefined}
    >
      {label}
      <ArrowUpRight size={12} className="opacity-70" />
    </Link>
  ) : (
    <div className={`px-6 py-2 text-[11px] font-semibold uppercase tracking-widest ${palette.label}`}>
      {label}
    </div>
  );
  return (
    <div className={`${palette.bg} border-t border-[#EBE6E0]`} data-testid={testId}>
      {header}
      <div className="divide-y divide-white/60">
        {children}
      </div>
    </div>
  );
}

function Row({ label, sublabel, amount, sign, palette, testId }) {
  const value = sign === "-" ? -Math.abs(amount) : sign === "+" ? Math.abs(amount) : amount;
  const displayText = sign === "+" ? `+${formatMoney(Math.abs(amount))}` : sign === "-" ? `-${formatMoney(Math.abs(amount))}` : formatMoney(value);
  return (
    <div className="px-6 py-2.5 flex items-center justify-between" data-testid={testId}>
      <div>
        <div className="text-[#2C302E] text-sm">{label}</div>
        {sublabel && <div className="text-xs text-[#8A918D] mt-0.5">{sublabel}</div>}
      </div>
      <div className={`text-sm font-semibold whitespace-nowrap ${palette.amount}`}>
        {displayText}
      </div>
    </div>
  );
}

function SubRow({ label, sublabel, amount, sign, palette, testId }) {
  const displayText = sign === "-" ? `-${formatMoney(Math.abs(amount))}` : formatMoney(amount);
  return (
    <div className="px-10 py-1.5 flex items-center justify-between bg-white/40" data-testid={testId}>
      <div>
        <div className="text-[#59605C] text-xs italic">{label}</div>
        {sublabel && <div className="text-[10px] text-[#8A918D] mt-0.5">{sublabel}</div>}
      </div>
      <div className={`text-xs font-medium whitespace-nowrap ${palette.amount}`}>
        {displayText}
      </div>
    </div>
  );
}

function TotalRow({ label, amount, sign, palette, testId }) {
  const displayText = sign === "+" ? `+${formatMoney(Math.abs(amount))}` : sign === "-" ? `-${formatMoney(Math.abs(amount))}` : formatMoney(amount);
  return (
    <div className={`px-6 py-2.5 flex items-center justify-between border-t border-white/70`} data-testid={testId}>
      <div className={`text-sm font-semibold ${palette.label}`}>{label}</div>
      <div className={`text-sm font-heading font-semibold whitespace-nowrap ${palette.amount}`}>{displayText}</div>
    </div>
  );
}

function MutedRow({ label }) {
  return (
    <div className="px-6 py-2.5 text-sm text-[#8A918D] italic">{label}</div>
  );
}

// A bill total row whose amount, when clicked, expands into a list of the
// underlying bills so the user can quickly see "which bills are these?" and
// jump to Manage Bills to act on them. Used for both Prior-month and
// Current-month unpaid sub-totals in the Monthly Outlook.
function ExpandableBillsRow({ label, sublabel, amount, bills, palette, testId }) {
  const [open, setOpen] = useState(false);
  const clickable = bills && bills.length > 0;
  const displayText = `-${formatMoney(Math.abs(amount))}`;
  const remaining = (b) =>
    Math.max(0, Number(b.estimated_due_amount || 0) - Number(b.paid_amount || 0));
  const sorted = clickable
    ? [...bills].sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""))
    : [];
  return (
    <div data-testid={testId}>
      <button
        type="button"
        onClick={() => clickable && setOpen((v) => !v)}
        disabled={!clickable}
        className={`w-full px-6 py-2.5 flex items-center justify-between text-left ${
          clickable ? "hover:bg-white/40 cursor-pointer" : "cursor-default"
        }`}
        data-testid={`${testId}-toggle`}
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 min-w-0">
          {clickable && (
            open ? <ChevronDown size={14} className={palette.label} /> : <ChevronRight size={14} className={palette.label} />
          )}
          <div className="min-w-0">
            <div className="text-[#2C302E] text-sm">{label}</div>
            {sublabel && <div className="text-xs text-[#8A918D] mt-0.5">{sublabel}</div>}
          </div>
        </div>
        <div className={`text-sm font-semibold whitespace-nowrap ${palette.amount} ${clickable ? "underline underline-offset-2 decoration-dotted" : ""}`}>
          {displayText}
        </div>
      </button>
      {open && clickable && (
        <div className="bg-white/40 divide-y divide-white/60" data-testid={`${testId}-list`}>
          {sorted.map((b) => (
            <Link
              key={b.id}
              to="/app/bills"
              className="px-10 py-1.5 flex items-center justify-between gap-3 hover:bg-white/60 transition-colors"
              data-testid={`${testId}-bill-${b.id}`}
              title="Open Manage Bills"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Receipt size={11} className="text-[#8A918D] shrink-0" />
                <span className="text-xs text-[#59605C] whitespace-nowrap">{formatDate(b.due_date)}</span>
                <span className="text-xs text-[#2C302E] truncate">{b.name}</span>
                {b.is_autopay && (
                  <span className="text-[9px] uppercase tracking-widest text-[#3B5B3E]">autopay</span>
                )}
              </div>
              <span className={`text-xs font-medium whitespace-nowrap ${palette.amount}`}>
                {formatMoney(remaining(b))}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
